/**
 * Appens pictogrammer — ét sæt, én stregstil.
 *
 * Alle tegninger ligger på det samme 24×24-gitter, har samme stregtykkelse
 * (1,75) og de samme runde hjørner. Det er hele pointen: blandes ikoner fra
 * flere kilder, kan øjet se det med det samme, også når det ikke kan sætte
 * ord på hvorfor.
 *
 * Tre valg, der er værd at kende:
 *
 * 1. **Ikonet arver tekstfarven** (`currentColor`). Et pictogram kan derfor
 *    aldrig komme til at sige noget andet end teksten ved siden af.
 * 2. **Uden `label` er ikonet skjult for skærmlæsere.** Et pictogram, der
 *    står ved siden af sin egen tekst, skal ikke læses op to gange. Står
 *    ikonet alene, SKAL kaldet give et `label` — så bliver det til et
 *    billede med et tilgængeligt navn.
 * 3. **`active` gør stregen tykkere og lægger en svag fyldning i formen.**
 *    Det er den udfyldte variant, aktiv navigation bruger — uden at der
 *    skal vedligeholdes to tegninger pr. ikon.
 */

/** Navnene på de pictogrammer, appen har. */
export type IconName =
  | 'home'
  | 'teams'
  | 'calendar'
  | 'message'
  | 'profile'
  | 'gymnastics'
  | 'swimming'
  | 'badminton'
  | 'minigolf'
  | 'aquapunkt'
  | 'aquaspinning'
  | 'motion'
  | 'clock'
  | 'place'
  | 'trainer'
  | 'family'
  | 'absence'
  | 'attendance'
  | 'volunteer'
  | 'stats'
  | 'roles'
  | 'settings'
  | 'conventus'
  | 'season'
  | 'overview'
  | 'info'
  | 'warning'
  | 'error'
  | 'success'
  | 'bell'
  | 'logout'
  | 'back'
  | 'forward'
  | 'plus'
  | 'search';

/**
 * Tegningerne. Hver værdi er de streger, ikonet består af — intet andet.
 * Fyld, farve og tykkelse sættes ét sted længere nede, så et nyt ikon
 * ikke kan komme til at falde uden for stilen.
 */
const PATHS: Record<IconName, string[]> = {
  // Hus
  home: ['M3.5 10.2 12 3.8l8.5 6.4', 'M5.5 9.2V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.2', 'M9.8 20v-5.4h4.4V20'],
  // To personer — hold
  teams: [
    'M9 11.5a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z',
    'M2.8 19.3c0-3 2.8-4.8 6.2-4.8s6.2 1.8 6.2 4.8',
    'M16.4 5.2a3 3 0 0 1 0 5.9',
    'M18 14.9c2 .6 3.2 1.9 3.2 3.6',
  ],
  // Kalenderblad
  calendar: [
    'M4.2 6.6h15.6a1 1 0 0 1 1 1v11.2a1 1 0 0 1-1 1H4.2a1 1 0 0 1-1-1V7.6a1 1 0 0 1 1-1Z',
    'M3.2 10.8h17.6',
    'M8 4.2v3.4',
    'M16 4.2v3.4',
  ],
  // Konvolut
  message: [
    'M3.4 6.4h17.2a1 1 0 0 1 1 1v9.2a1 1 0 0 1-1 1H3.4a1 1 0 0 1-1-1V7.4a1 1 0 0 1 1-1Z',
    'M2.8 7.6 12 13.4l9.2-5.8',
  ],
  // Person
  profile: [
    'M12 11.6a3.7 3.7 0 1 0 0-7.4 3.7 3.7 0 0 0 0 7.4Z',
    'M4.6 20.2c0-3.4 3.3-5.5 7.4-5.5s7.4 2.1 7.4 5.5',
  ],
  // Gymnastik: figur i spring
  gymnastics: [
    'M13.4 5.6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
    'M4.4 12.2 8.6 9l3.6 1.6 2.2 3.2',
    'M14.4 13.8 12 20.4',
    'M14.4 13.8l4.8 1.2',
    'M8.6 9 6.4 15.6',
  ],
  // Svømning: bølger
  swimming: [
    'M2.6 17.4c1.5 0 1.5 1.3 3 1.3s1.5-1.3 3-1.3 1.5 1.3 3 1.3 1.5-1.3 3-1.3 1.5 1.3 3 1.3 1.5-1.3 3-1.3',
    'M6.2 15V8.4l6.4-2.6',
    'M12.6 5.8 17 8.2',
    'M17.4 6.4a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8Z',
  ],
  /*
   * Badminton: ketsjeren, ikke fjerbolden.
   *
   * Fjerbolden blev tegnet først — kork, skørt og seks streger. Ved 16 px,
   * som er den størrelse mærkaterne faktisk bruger, blev den til en klat.
   * Ketsjeren har tre streger og en silhuet, øjet kender.
   */
  badminton: [
    'M12 3.4c2.43 0 4.4 2.33 4.4 5.2s-1.97 5.2-4.4 5.2-4.4-2.33-4.4-5.2S9.57 3.4 12 3.4Z',
    'M12 13.8v5.6',
    'M10.5 19.4h3',
  ],
  // AquaPunkt: en vanddråbe. Svømning har bølgerne, så dråben er fri.
  aquapunkt: [
    'M12 3.6c3.2 3.7 5.2 6.4 5.2 8.9a5.2 5.2 0 0 1-10.4 0c0-2.5 2-5.2 5.2-8.9Z',
    'M9.6 13.4a2.5 2.5 0 0 0 2.4 3.1',
  ],
  // AquaSpinning: cyklen. To hjul og en ramme er til at kende ved 16 px.
  aquaspinning: [
    'M5.8 18.4a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z',
    'M18.2 18.4a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z',
    'M5.8 15.3 9.6 8.6h4.6l4 6.7',
    'M9.6 8.6h3.2',
  ],
  // Skals i Bevægelse: pulslinjen. Bevægelse uden at være endnu en figur.
  motion: [
    'M3.2 12.4h3.4l2.2-5.2 3.4 10 2.4-6.4 1.6 3.2h4.6',
  ],
  // Minigolf: flaget i hullet og bolden på banen.
  minigolf: [
    'M5 19.2h14',
    'M14.6 19.2V5',
    'M14.6 5.2 19.4 7.4l-4.8 2.2',
    'M8 15.9a1.65 1.65 0 1 0 0 3.3 1.65 1.65 0 0 0 0-3.3Z',
  ],
  // Ur
  clock: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M12 7.4V12l3 1.8'],
  // Kortnål
  place: [
    'M12 21c4-4.3 6-7.5 6-10.2A6 6 0 0 0 6 10.8C6 13.5 8 16.7 12 21Z',
    'M12 13a2.3 2.3 0 1 0 0-4.6A2.3 2.3 0 0 0 12 13Z',
  ],
  // Instruktør: person med flueben
  trainer: [
    'M10 11.4a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z',
    'M3 20c0-3.3 3.2-5.4 7-5.4 1 0 2 .2 2.9.4',
    'M14.8 17.6l2 2 3.8-4',
  ],
  // Familie: to personer, en stor og en lille
  family: [
    'M8.4 10.4a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
    'M2.8 20c0-2.9 2.5-4.6 5.6-4.6S14 17.1 14 20',
    'M17 13.6a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z',
    'M13.8 20c0-2.1 1.4-3.4 3.2-3.4s3.2 1.3 3.2 3.4',
  ],
  // Afbud: kryds i cirkel
  absence: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M9.2 9.2l5.6 5.6', 'M14.8 9.2l-5.6 5.6'],
  // Fremmøde: flueben i cirkel
  attendance: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M8.4 12.2l2.6 2.6 4.6-5'],
  /*
   * Frivillig: et hjerte. Den «hjælpende hånd», man normalt ville vælge,
   * bliver til en streg-krusedulle ved 18 px — og et pictogram, man skal
   * kigge to gange på, er ikke et pictogram.
   */
  volunteer: [
    'M12 19.6 4.9 12.7a4.2 4.2 0 0 1 0-6 4.3 4.3 0 0 1 6 0l1.1 1.1 1.1-1.1a4.3 4.3 0 0 1 6 0 4.2 4.2 0 0 1 0 6L12 19.6Z',
  ],
  // Statistik: søjler
  stats: ['M3.6 20.4h16.8', 'M7 20.4V13', 'M12 20.4V6.6', 'M17 20.4v-5.2'],
  // Roller: skjold med flueben
  roles: ['M12 3.4l7 2.6v5.4c0 4.2-2.9 7.4-7 9.2-4.1-1.8-7-5-7-9.2V6l7-2.6Z', 'M9.2 11.8l2.2 2.2 3.6-3.8'],
  // Indstillinger: skydeknapper
  settings: [
    'M4 7.4h10',
    'M18 7.4h2',
    'M4 16.6h4',
    'M12 16.6h8',
    'M16 9.4a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
    'M10 18.6a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  ],
  // Conventus: forbindelse mellem to systemer
  conventus: [
    'M9.6 14.4 14.4 9.6',
    'M13.2 6.6l1.4-1.4a3.7 3.7 0 0 1 5.2 5.2l-1.4 1.4',
    'M10.8 17.4l-1.4 1.4a3.7 3.7 0 0 1-5.2-5.2l1.4-1.4',
  ],
  // Sæson: kalender med gentagelse
  season: [
    'M4.2 6.6h15.6a1 1 0 0 1 1 1v11.2a1 1 0 0 1-1 1H4.2a1 1 0 0 1-1-1V7.6a1 1 0 0 1 1-1Z',
    'M3.2 10.8h17.6',
    'M7.6 14.6l1.8 1.8 3.6-3.8',
    'M15.6 15.4h2.8',
  ],
  // Overblik: felter
  overview: [
    'M4.2 4.6h5.2v5.2H4.2Z',
    'M14.6 4.6h5.2v5.2h-5.2Z',
    'M4.2 14.2h5.2v5.2H4.2Z',
    'M14.6 14.2h5.2v5.2h-5.2Z',
  ],
  // Information
  info: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M12 11.2v5', 'M12 7.9v.1'],
  // Advarsel: trekant
  warning: [
    'M10.7 4.3 3.3 17.1a1.5 1.5 0 0 0 1.3 2.3h14.8a1.5 1.5 0 0 0 1.3-2.3L13.3 4.3a1.5 1.5 0 0 0-2.6 0Z',
    'M12 9.4v4',
    'M12 16.4v.1',
  ],
  // Fejl
  error: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M12 7.8v5', 'M12 16.2v.1'],
  // Kvittering
  success: ['M12 20.6a8.6 8.6 0 1 0 0-17.2 8.6 8.6 0 0 0 0 17.2Z', 'M8.4 12.2l2.6 2.6 4.6-5'],
  // Klokke
  bell: [
    'M18 9.4a6 6 0 1 0-12 0c0 4.4-1.6 5.8-1.6 5.8h15.2S18 13.8 18 9.4Z',
    'M13.7 18.6a2 2 0 0 1-3.4 0',
  ],
  // Log ud
  logout: ['M9.4 20.4H5.6a1.4 1.4 0 0 1-1.4-1.4V5a1.4 1.4 0 0 1 1.4-1.4h3.8', 'M15.4 16.4l4.4-4.4-4.4-4.4', 'M19.2 12H9.2'],
  // Tilbage
  back: ['M19 12H5.4', 'M11 5.6 4.6 12l6.4 6.4'],
  // Frem
  forward: ['M9.4 5.6 15.8 12l-6.4 6.4'],
  // Tilføj
  plus: ['M12 5.2v13.6', 'M5.2 12h13.6'],
  // Søg
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'M16.2 16.2 20.4 20.4'],
};

/**
 * Former, der må fyldes ud i aktiv tilstand. Kun den YDRE form — fyldes
 * fluebenet inde i en cirkel også, forsvinder det.
 */
const FILLABLE: Partial<Record<IconName, number>> = {
  home: 1,
  teams: 0,
  calendar: 0,
  message: 0,
  profile: 0,
  attendance: 0,
  absence: 0,
  success: 0,
  info: 0,
  error: 0,
  warning: 0,
  roles: 0,
  bell: 0,
  volunteer: 0,
  place: 0,
  clock: 0,
  search: 0,
};

export interface IconProps {
  name: IconName;
  /** Pixelstørrelse. 20 i tekst, 24 i navigation, 18 i mærkater. */
  size?: number;
  /**
   * Tilgængeligt navn. SKAL sættes, når ikonet står uden tekst ved siden
   * af. Udelades det, skjules ikonet for skærmlæsere — hvilket er det
   * rigtige, når teksten allerede står der.
   */
  label?: string;
  /** Aktiv tilstand: tykkere streg og en svag fyldning. */
  active?: boolean;
  className?: string;
}

export function Icon({
  name,
  size = 20,
  label,
  active = false,
  className,
}: IconProps): JSX.Element {
  const paths = PATHS[name];
  const fillIndex = active ? FILLABLE[name] : undefined;
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.1 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label ? <title>{label}</title> : null}
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill={i === fillIndex ? 'currentColor' : 'none'}
          fillOpacity={i === fillIndex ? 0.16 : undefined}
        />
      ))}
    </svg>
  );
}

/** Pictogram i en rolig flade — bruges foran genveje og i tomme tilstande. */
export function IconBadge({
  name,
  tone = 'red',
  label,
  size = 20,
}: {
  name: IconName;
  tone?: 'red' | 'swim' | 'neutral';
  label?: string;
  size?: number;
}): JSX.Element {
  const suffix = tone === 'swim' ? ' icon-badge--swim' : tone === 'neutral' ? ' icon-badge--neutral' : '';
  return (
    <span className={`icon-badge${suffix}`}>
      <Icon name={name} size={size} label={label} />
    </span>
  );
}

/**
 * Afdelingens pictogram. Hver afdeling har sit eget — farven må aldrig
 * være det eneste, der skiller dem ad.
 *
 * Ukendte værdier falder tilbage på gymnastikikonet frem for at tegne
 * ingenting. Et manglende ikon ville flytte teksten ved siden af og få
 * en hel liste til at se i stykker ud.
 */
const DEPARTMENT_ICONS: Record<string, IconName> = {
  gymnastik: 'gymnastics',
  svomning: 'swimming',
  badminton: 'badminton',
  minigolf: 'minigolf',
  aquapunkt: 'aquapunkt',
  aquaspinning: 'aquaspinning',
  skalsibevaegelse: 'motion',
};

export function departmentIcon(department: string): IconName {
  return DEPARTMENT_ICONS[department] ?? 'gymnastics';
}
